import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, ChevronDown, ChevronUp, X, Gamepad2, Filter } from 'lucide-react';
import API from '@/utils/api';
import ListingCard from '@/components/ListingCard';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest / Bumped' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'popular', label: 'Most Viewed' },
];

// ── defined OUTSIDE Browse so React never unmounts them on state change ──
function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#1a1a1a]">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 px-4 hover:bg-white/[0.02] transition-colors text-left group">
        <span className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-400">{title}</span>
        {open ? <ChevronUp size={12} className="text-zinc-700" /> : <ChevronDown size={12} className="text-zinc-700" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function Sidebar({ games, selectedGame, setSelectedGame, filterValues, setFilterValues,
  priceMin, setPriceMin, priceMax, setPriceMax, setPage, activeFilterCount, clearFilters,
  setSearchParams, gameFilters }) {

  // ── CRITICAL: useRef so input value doesn't cause remount ──
  const [gameSearch, setGameSearch] = useState('');
  const searchRef = useRef(null);

  const filteredGames = games.filter(g =>
    g.name.toLowerCase().includes(gameSearch.toLowerCase())
  );

  return (
    <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#1a1a1a]">
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-rm-red" />
          <span className="font-rajdhani font-bold text-sm uppercase tracking-widest">Filters</span>
        </div>
        {activeFilterCount > 0 && (
          <button onClick={clearFilters} data-testid="clear-filters"
            className="flex items-center gap-1 text-[10px] text-rm-red hover:text-red-400 transition-colors uppercase tracking-widest">
            <X size={10} /> Clear ({activeFilterCount})
          </button>
        )}
      </div>

      {/* GAME */}
      <FilterSection title="Game">
        <div className="relative mb-2">
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-700" />
          <input
            ref={searchRef}
            value={gameSearch}
            onChange={e => setGameSearch(e.target.value)}
            placeholder="Search games..."
            data-testid="game-search"
            className="w-full bg-[#0a0a0a] border border-[#1e1e1e] text-zinc-300 placeholder:text-zinc-700 pl-7 pr-3 py-2 rounded-sm text-xs"
          />
        </div>
        <button onClick={() => { setSelectedGame(''); setPage(1); setSearchParams({}); }}
          data-testid="game-all"
          className={`w-full text-left px-3 py-2 rounded-sm text-xs font-medium transition-all mb-1 flex items-center gap-2
            ${!selectedGame ? 'bg-rm-red/10 text-rm-red border border-rm-red/20' : 'text-zinc-500 hover:text-white hover:bg-white/[0.03]'}`}>
          <Gamepad2 size={11} /> All Games
        </button>
        <div className="space-y-0.5 max-h-52 overflow-y-auto pr-1">
          {filteredGames.map(g => (
            <button key={g.id} data-testid={`game-filter-${g.id}`}
              onClick={() => { setSelectedGame(g.id); setPage(1); setSearchParams({ game: g.id }); }}
              className={`w-full text-left px-3 py-1.5 rounded-sm text-xs transition-all flex items-center gap-2
                ${selectedGame === g.id
                  ? 'bg-rm-red/10 text-rm-red border border-rm-red/20'
                  : 'text-zinc-500 hover:text-white hover:bg-white/[0.03]'}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${selectedGame === g.id ? 'bg-rm-red' : 'bg-zinc-700'}`} />
              {g.name}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* PRICE */}
      <FilterSection title="Price Range">
        <div className="space-y-2">
          <input type="number" placeholder="Min price ($)" value={priceMin}
            onChange={e => { setPriceMin(e.target.value); setPage(1); }}
            data-testid="price-min"
            className="w-full bg-[#0a0a0a] border border-[#1e1e1e] text-zinc-300 placeholder:text-zinc-700 px-2 py-2 rounded-sm text-xs" />
          <input type="number" placeholder="Max price ($)" value={priceMax}
            onChange={e => { setPriceMax(e.target.value); setPage(1); }}
            data-testid="price-max"
            className="w-full bg-[#0a0a0a] border border-[#1e1e1e] text-zinc-300 placeholder:text-zinc-700 px-2 py-2 rounded-sm text-xs" />
        </div>
      </FilterSection>

      {/* GAME FILTERS */}
      {gameFilters.length > 0 && (
        <FilterSection title="Item Filters">
          <div className="space-y-3">
            {gameFilters.map(f => (
              <div key={f.name}>
                <label className="block text-[9px] uppercase tracking-[0.15em] text-zinc-700 mb-1.5">{f.name}</label>
                {f.type === 'select' && f.options?.length > 0 ? (
                  <select value={filterValues[f.name] || ''}
                    onChange={e => setFilterValues(prev => ({ ...prev, [f.name]: e.target.value }))}
                    data-testid={`filter-${f.name}`}
                    className="w-full bg-[#0a0a0a] border border-[#1e1e1e] text-zinc-300 px-2 py-2 rounded-sm text-xs">
                    <option value="">Any {f.name}</option>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type="text" placeholder={`Any ${f.name}...`}
                    value={filterValues[f.name] || ''}
                    onChange={e => setFilterValues(prev => ({ ...prev, [f.name]: e.target.value }))}
                    data-testid={`filter-${f.name}`}
                    className="w-full bg-[#0a0a0a] border border-[#1e1e1e] text-zinc-300 placeholder:text-zinc-700 px-2 py-2 rounded-sm text-xs" />
                )}
              </div>
            ))}
          </div>
        </FilterSection>
      )}
    </div>
  );
}

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(searchParams.get('game') || '');
  const [gameFilters, setGameFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [sort, setSort] = useState('newest');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [listingTypes, setListingTypes] = useState([]);   // [] = all
  const searchQuery = searchParams.get('q') || '';
  const LIMIT = 12;

  useEffect(() => {
    API.get('/games').then(r => setGames(r.data));
  }, []);

  useEffect(() => {
    if (selectedGame) {
      const game = games.find(g => g.id === selectedGame);
      setGameFilters(game?.filters || []);
      setFilterValues({});
    } else {
      setGameFilters([]);
      setFilterValues({});
    }
  }, [selectedGame, games]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, sort_by: sort });
      if (selectedGame) params.set('game_id', selectedGame);
      if (searchQuery) params.set('search', searchQuery);
      if (priceMin) params.set('min_price', priceMin);
      if (priceMax) params.set('max_price', priceMax);
      if (listingTypes.length === 1) params.set('listing_type', listingTypes[0]);
      const active = Object.entries(filterValues).filter(([, v]) => v?.trim());
      if (active.length) params.set('filters', JSON.stringify(Object.fromEntries(active)));
      const { data } = await API.get(`/listings?${params}`);
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch { setListings([]); }
    finally { setLoading(false); }
  }, [page, selectedGame, sort, priceMin, priceMax, filterValues, searchQuery, listingTypes]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const clearFilters = () => {
    setSelectedGame(''); setFilterValues({});
    setPriceMin(''); setPriceMax(''); setSort('newest'); setPage(1);
    setSearchParams({});
  };

  const activeFilterCount = [selectedGame, priceMin, priceMax, searchQuery,
    ...Object.values(filterValues).filter(Boolean)].filter(Boolean).length;

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-1">Marketplace</p>
          <div className="flex items-end justify-between">
            <h1 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight" data-testid="browse-title">
              {searchQuery ? <>Results for "<span className="text-rm-red">{searchQuery}</span>"</> : <>Browse <span className="text-zinc-600">Listings</span></>}
            </h1>
            <span className="text-sm text-zinc-600 hidden sm:block">{total} results</span>
          </div>
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center justify-between mb-4 md:hidden">
          <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="flex items-center gap-2 btn-outline px-4 py-2 text-sm rounded-sm">
            <SlidersHorizontal size={14} /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
          <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
            className="bg-[#111] border border-[#222] text-zinc-300 px-3 py-2 rounded-sm text-xs">
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {mobileFiltersOpen && (
          <div className="md:hidden mb-6 animate-fade-in">
            <Sidebar games={games} selectedGame={selectedGame} setSelectedGame={setSelectedGame}
              filterValues={filterValues} setFilterValues={setFilterValues}
              priceMin={priceMin} setPriceMin={setPriceMin} priceMax={priceMax} setPriceMax={setPriceMax}
              setPage={setPage} activeFilterCount={activeFilterCount} clearFilters={clearFilters}
              setSearchParams={setSearchParams} gameFilters={gameFilters} />
          </div>
        )}

        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-56 shrink-0">
            <Sidebar games={games} selectedGame={selectedGame} setSelectedGame={setSelectedGame}
              filterValues={filterValues} setFilterValues={setFilterValues}
              priceMin={priceMin} setPriceMin={setPriceMin} priceMax={priceMax} setPriceMax={setPriceMax}
              setPage={setPage} activeFilterCount={activeFilterCount} clearFilters={clearFilters}
              setSearchParams={setSearchParams} gameFilters={gameFilters} />
          </aside>

          {/* Grid */}
          <div className="flex-1 min-w-0">
            {/* Search + Sort bar */}
            <div className="flex items-center gap-3 mb-3">
              <form onSubmit={e => { e.preventDefault(); const q = e.target.search.value.trim(); setSearchParams(q ? { ...(selectedGame ? { game: selectedGame } : {}), q } : (selectedGame ? { game: selectedGame } : {})); setPage(1); }}
                className="flex-1">
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
                  <input
                    name="search"
                    type="text"
                    defaultValue={searchQuery}
                    key={searchQuery}
                    placeholder="Search listings, games, tags..."
                    data-testid="browse-search"
                    className="w-full bg-[#111] border border-[#1e1e1e] text-white placeholder:text-zinc-700 pl-8 pr-3 py-2 rounded-sm text-sm focus:border-rm-red/50 focus:outline-none transition-colors"
                  />
                </div>
              </form>
              <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
                data-testid="sort-select"
                className="hidden md:block bg-[#111] border border-[#1e1e1e] text-zinc-300 px-3 py-2 rounded-sm text-xs hover:border-[#2a2a2a] transition-colors shrink-0">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Listing Type Filter Chips */}
            {(() => {
              const TYPE_FILTERS = [
                { value: 'retail', label: 'Single', color: 'border-zinc-700 hover:border-zinc-500 data-[active=true]:border-zinc-400 data-[active=true]:bg-zinc-800/50 data-[active=true]:text-white' },
                { value: 'pack', label: 'Pack / Bulk', color: 'border-zinc-700 hover:border-blue-500/50 data-[active=true]:border-blue-500 data-[active=true]:bg-blue-500/10 data-[active=true]:text-blue-300' },
                { value: 'auction', label: 'Auction', color: 'border-zinc-700 hover:border-amber-500/50 data-[active=true]:border-amber-500 data-[active=true]:bg-amber-500/10 data-[active=true]:text-amber-300' },
              ];
              const toggle = (v) => {
                setListingTypes(prev => {
                  const next = prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v];
                  return next.length === 3 ? [] : next;
                });
                setPage(1);
              };
              return (
                <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="type-filter-bar">
                  <span className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.2em] text-zinc-600 shrink-0">Type</span>
                  {TYPE_FILTERS.map(t => {
                    const isActive = listingTypes.includes(t.value);
                    return (
                      <button key={t.value}
                        data-active={isActive}
                        data-testid={`type-filter-${t.value}`}
                        onClick={() => toggle(t.value)}
                        className={`px-3 py-1 rounded-sm text-xs font-medium border text-zinc-500 transition-all duration-150 ${t.color}`}>
                        {t.label}
                      </button>
                    );
                  })}
                  {listingTypes.length > 0 && (
                    <button onClick={() => { setListingTypes([]); setPage(1); }}
                      className="px-2 py-1 text-[10px] text-zinc-700 hover:text-zinc-400 transition-colors">
                      clear
                    </button>
                  )}
                </div>
              );
            })()}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm overflow-hidden">
                    <div className="aspect-[4/3] bg-[#111] animate-shimmer" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-[#151515] rounded animate-shimmer w-3/4" />
                      <div className="h-3 bg-[#151515] rounded animate-shimmer w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-24 border border-[#1a1a1a] rounded-sm bg-[#0d0d0d]">
                <Gamepad2 size={36} className="mx-auto mb-4 text-zinc-800" />
                <p className="font-rajdhani font-bold text-lg uppercase text-zinc-600">No listings found</p>
                <p className="text-xs text-zinc-700 mt-1">Try adjusting your filters</p>
                {activeFilterCount > 0 && (
                  <button onClick={clearFilters} className="btn-outline px-6 py-2.5 text-sm rounded-sm mt-4">Clear Filters</button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {listings.map((l, i) => (
                  <div key={l.id} className={`animate-fade-in delay-${Math.min(i * 50, 300)}`}>
                    <ListingCard listing={l} />
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-10" data-testid="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                  className="px-3 py-2 text-xs border border-[#222] text-zinc-500 hover:border-[#333] hover:text-white disabled:opacity-30 rounded-sm transition-all font-rajdhani font-bold uppercase tracking-wider">
                  Prev
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`w-9 h-9 text-xs border rounded-sm transition-all font-rajdhani font-bold
                        ${page === p ? 'bg-rm-red border-rm-red text-white' : 'border-[#222] text-zinc-600 hover:border-[#333] hover:text-white'}`}>
                      {p}
                    </button>
                  );
                })}
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
                  className="px-3 py-2 text-xs border border-[#222] text-zinc-500 hover:border-[#333] hover:text-white disabled:opacity-30 rounded-sm transition-all font-rajdhani font-bold uppercase tracking-wider">
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
